import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Anchor, Loader2, Waves, Clock, ChevronLeft, BookmarkCheck, BookOpenCheck, MapPin, Gauge, Thermometer, Hourglass } from 'lucide-react';
import PwaInstallBanner from '../../components/PwaInstallBanner';
import { viewerStore } from './viewerStore';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const subKitUp = (t) => {
  if (!t || !/^\d{2}:\d{2}$/.test(t)) return '';
  const [h, m] = t.split(':').map(Number);
  const total = h * 60 + m - 60;
  if (total < 0) return '';
  return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
};

const PlannerShare = () => {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [savedFlag, setSavedFlag] = useState(false);

  useEffect(() => {
    let active = true;
    axios.get(`${API_URL}/planner/share/${token}`)
      .then((r) => {
        if (!active) return;
        setData(r.data);
        // Auto-save this share so the viewer's installed PWA remembers it.
        viewerStore.add({ token, trip_name: r.data.trip_name, date: r.data.date });
        setSavedFlag(true);
      })
      .catch(() => active && setError('Plan not found or no longer shared.'))
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [token]);

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-slate-50"><Loader2 className="w-8 h-8 animate-spin text-[#0A2342]" /></div>;
  if (error) return <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6 text-center text-gray-500">{error}</div>;
  if (!data) return null;

  return (
    <div className="min-h-screen bg-slate-50 pb-32" data-testid="planner-share">
      <div className="bg-gradient-to-br from-[#0A2342] to-[#1a3a5c] text-white px-5 pt-10 pb-8">
        <div className="max-w-3xl mx-auto">
          {viewerStore.list().length >= 1 && (
            <Link to="/planner/my-dives" className="text-xs opacity-80 flex items-center gap-1 mb-3 hover:opacity-100">
              <ChevronLeft className="w-3.5 h-3.5" /> My logged dives
            </Link>
          )}
          <div className="text-center">
            <Waves className="w-10 h-10 text-[#FF6B35] mx-auto mb-2" />
            <h1 className="text-2xl font-bold">{data.trip_name}</h1>
            <p className="text-sm opacity-80 mt-1">Dive launch schedule — {data.date}</p>
            {savedFlag && (
              <p className="text-[11px] mt-2 inline-flex items-center gap-1 bg-white/10 px-2 py-0.5 rounded-full">
                <BookmarkCheck className="w-3 h-3" /> Saved to this device — install the app to access it anytime
              </p>
            )}
          </div>
        </div>
      </div>
      <div className="max-w-3xl mx-auto px-4 mt-4 space-y-4">
        {data.warnings?.length > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-800">
            ⚠ {data.warnings.join(' • ')}
          </div>
        )}
        {(() => {
          // Group launches by boat for display
          const boats = data.boats && data.boats.length > 0
            ? data.boats
            : [{ index: 0, name: 'Boat A', launches_count: data.launches_count, capacity_per_launch: data.capacity_per_launch }];
          const grouped = boats.map((b, bi) => ({
            boat: b,
            boat_index: b.index ?? bi,
            launches: data.launches.filter((L) => (L.boat_index ?? 0) === (b.index ?? bi)),
          }));
          return grouped.map((g, gi) => (
            <div key={`bg-${gi}`} className="space-y-3">
              {grouped.length > 1 && (
                <div className="flex items-center gap-2 pt-1">
                  <Anchor className="w-5 h-5 text-[#FF6B35]" />
                  <h2 className="text-lg font-bold text-[#0A2342]">{g.boat.name || `Boat ${String.fromCharCode(65 + gi)}`}</h2>
                </div>
              )}
              {g.launches.map((L) => {
                // Backend public-share endpoint only sends kit-up times under
                // `display_times` — raw launch times are intentionally absent.
                const displayTime = data.display_times?.[L.index] || '';
                const launchLabel = L.launch_in_boat != null ? L.launch_in_boat + 1 : L.index + 1;
                return (
                  <div key={L.index} className="bg-white border rounded-2xl overflow-hidden shadow-sm" data-testid={`share-launch-${L.index}`}>
                    <div className="bg-[#0A2342] text-white px-4 py-3 flex items-center gap-2">
                      <Anchor className="w-5 h-5 text-[#FF6B35]" />
                      <h2 className="font-semibold">Launch {launchLabel}</h2>
                    </div>
                    {displayTime && (
                      <div className="bg-slate-50 px-4 py-2 text-xs flex items-center gap-3 border-b">
                        <Clock className="w-3.5 h-3.5 text-[#FF6B35]" />
                        <span><strong>Time:</strong> {displayTime}</span>
                      </div>
                    )}
                    <div className="px-4 py-3 border-b">
                      <div className="text-xs font-semibold text-gray-500 mb-1">CREW</div>
                      {L.crew.length === 0 ? <div className="text-xs text-red-500">No crew</div> : L.crew.map((c) => (
                        <div key={c.name} className="text-sm font-medium text-[#0A2342]">{c.name} <span className="text-xs text-gray-400 capitalize">· {c.role}</span></div>
                      ))}
                    </div>
                    <div className="px-4 py-3">
                      <div className="text-xs font-semibold text-gray-500 mb-1">DIVERS ({L.divers.length})</div>
                      {L.divers.map((d, i) => (
                        <div key={d.name + i} className="text-sm text-[#0A2342] flex items-center gap-2 py-0.5">
                          <span className="text-xs text-gray-400 w-5">{i + 1}.</span> {d.name}
                          {d.certification && <span className="text-xs text-gray-400">— {d.certification}</span>}
                        </div>
                      ))}
                    </div>
                    {L.log && (
                      <div className="bg-slate-50 border-t px-4 py-3" data-testid={`share-log-${L.index}`}>
                        <div className="text-xs font-semibold text-[#0A2342] flex items-center gap-1 mb-1">
                          <BookOpenCheck className="w-3.5 h-3.5 text-[#FF6B35]" /> Instructor's Dive Log
                        </div>
                        <p className="text-[11px] text-gray-500 italic leading-snug mb-1">
                          NOTE: The following information is from <strong>{L.log.recorded_by_name || 'Crew'}</strong> and is here for reference only.
                        </p>
                        <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-[#0A2342]">
                          {L.log.date && <span><Clock className="w-3 h-3 inline" /> {L.log.date}{L.log.time ? ` ${L.log.time}` : ''}</span>}
                          {L.log.site && <span><MapPin className="w-3 h-3 inline" /> {L.log.site}</span>}
                          {L.log.depth_m != null && <span><Gauge className="w-3 h-3 inline" /> {L.log.depth_m} m</span>}
                          {L.log.water_temp_c != null && <span><Thermometer className="w-3 h-3 inline" /> {L.log.water_temp_c}°C</span>}
                          {L.log.duration_min != null && <span><Hourglass className="w-3 h-3 inline" /> {L.log.duration_min} min</span>}
                        </div>
                        {L.log.notes && <p className="text-xs text-gray-600 mt-1">{L.log.notes}</p>}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ));
        })()}
        <p className="text-center text-xs text-gray-400 mt-6">Just Dive · justdive.co.za</p>
      </div>
      <PwaInstallBanner />
    </div>
  );
};

export default PlannerShare;
